import type { CSSProperties, ReactNode } from 'react'
import { chakra } from '@chakra-ui/react'
import { Pressable } from '@ayble-health/dashboard-components'
import { Button } from 'components/ui/button'
import { Chip, type ChipVariant } from 'components/ui/chip'
import {
  FileIcon, ClipboardIcon, MessageIcon, MoreVerticalIcon,
  ChevronRight, ChevronDown, ChevronsRight, PanelToggleIcon,
} from './icons'
import { RiskDots, type RiskLevel } from './RiskDots'

// C_Sidebar/Patient (Figma — Design System 2.1, node 40005215:8991)
// Spec: docs/design/C_sidebar-patient-design-spec.md

// ─── Types ─────────────────────────────────────────────────────────────────

export type { RiskLevel } // re-export — the canonical definition moved to RiskDots.tsx
export type MeetingStatus = 'upcoming' | 'billed' | 'no-show'

export interface SidebarPatient {
  age: number
  sex: string
  ethnicity: string
  dob: string
  memberSince: string
  membershipType: string
  healthieId: string
  nextVisit: {
    date: string
    status: 'Confirmed' | 'Unconfirmed' | 'In Progress'
    type: string
    provider: string
    quote?: string
  }
  detailRows: { label: string; value: string; moreLabel?: string }[]
  careTeam: { initials: string; name: string; role: string; bg: string }[]
  subscriptionAccount: string[]
  riskHistory: { level: RiskLevel; date: string }[]
  meetingHistory: { date: string; type: string; status: MeetingStatus }[]
}

interface Props {
  patient: SidebarPatient
  collapsed?: boolean
  onToggleCollapse?: () => void
}

// careOS interactive chrome with no ui/Button equivalent (hybrid one-offs).
// These use Pressable — the package's reset native button — rather than
// chakra.button, so they still sit on the shared interactive primitive.
// Pressable's own styles are column + stretch; row chrome has to set
// flexDirection="row" as a Chakra prop (css alone loses to the default).

// "tertiary" — top-bordered disclosure row with trailing caret. Full-width,
// space-between, transparent, aqua → seafoam on hover.
function TertiaryButton({ children, endIcon, onClick, style }: {
  children: ReactNode; endIcon?: ReactNode; onClick?: () => void; style?: CSSProperties
}) {
  return (
    <Pressable
      type="button"
      onClick={onClick}
      flexDirection="row"
      alignItems="center"
      css={{
        height: '40px', width: '100%',
        background: 'transparent', color: 'aqua',
        borderTop: '1px solid', borderTopColor: 'cloud',
        display: 'inline-flex', justifyContent: 'space-between',
        fontFamily: 'body', cursor: 'pointer',
        _hover: { color: 'seafoam' },
        _active: { color: 'aqua' },
      }}
      style={{ padding: 0, fontSize: 'var(--text-cos-btn-sb-size)', fontWeight: 'var(--text-cos-btn-sb-weight)', lineHeight: 1, ...style }}
    >
      <span>{children}</span>
      {endIcon && <span style={{ display: 'inline-flex' }}>{endIcon}</span>}
    </Pressable>
  )
}

// "icon-tile" — 44px tile: start icon + label + trailing caret, with part-level
// colours (start blue-grey, end seafoam) that collapse to evergreen on hover.
function IconTileButton({ startIcon, children, endIcon, onClick }: {
  startIcon: ReactNode; children: ReactNode; endIcon?: ReactNode; onClick?: () => void
}) {
  return (
    <Pressable
      type="button"
      onClick={onClick}
      flexDirection="row"
      alignItems="center"
      css={{
        height: '44px', width: '100%',
        borderRadius: 'card', background: 'canvas', color: 'evergreen',
        border: '1px solid', borderColor: 'cloud',
        display: 'inline-flex', justifyContent: 'space-between',
        gap: '8px', fontFamily: 'body', cursor: 'pointer',
        '& .tile-start': { color: 'bluegrey', display: 'inline-flex' },
        '& .tile-end': { color: 'seafoam', display: 'inline-flex' },
        _hover: {
          background: 'feather', borderColor: 'steel', color: 'evergreen',
          '& .tile-start': { color: 'evergreen' }, '& .tile-end': { color: 'evergreen' },
        },
        _active: {
          background: 'feather', borderColor: 'seafoam', color: 'evergreen',
          '& .tile-start': { color: 'evergreen' }, '& .tile-end': { color: 'evergreen' },
        },
      }}
      style={{ padding: '0 16px', fontSize: 14, fontWeight: 400, lineHeight: 1.25, letterSpacing: '0.01em' }}
    >
      <span className="tile-start">{startIcon}</span>
      <span className="tile-label" style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>{children}</span>
      <span className="tile-end">{endIcon}</span>
    </Pressable>
  )
}

// ─── Risk dots ──────────────────────────────────────────────────────────────
// Moved to RiskDots.tsx (shared with the pre-visit claims insight rows);
// imported above.

// ─── Section A0 — Patient Details Header (rail top) ───────────────────────

function CollapseToggle({ collapsed, onClick }: { collapsed: boolean; onClick?: () => void }) {
  return (
    <Pressable
      type="button"
      onClick={onClick}
      aria-label={collapsed ? 'Expand patient rail' : 'Collapse patient rail'}
      w="32px"
      h="32px"
      flexShrink={0}
      borderRadius="card"
      bg="feather"
      color="seafoam"
      display="inline-flex"
      alignItems="center"
      justifyContent="center"
    >
      <ChevronsRight direction={collapsed ? 'left' : 'right'} />
    </Pressable>
  )
}

function PatientDetailsHeader({ patient, collapsed, onToggle }: {
  patient: SidebarPatient
  collapsed: boolean
  onToggle?: () => void
}) {
  const labelStyle: CSSProperties = { color: 'var(--blue-grey)' }
  const valueStyle: CSSProperties = { color: 'var(--evergreen)' }
  return (
    <div style={{
      width: '100%', height: collapsed ? 56 : 110,
      background: 'var(--canvas)',
      padding: 'var(--space-s)',
      display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4,
      flexShrink: 0,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
        <span style={{
          fontSize: 'var(--text-xs-size)', fontWeight: 'var(--text-xs-weight)', lineHeight: 'var(--text-xs-lh)', letterSpacing: '0.01em',
          color: 'var(--blue-grey)', textTransform: 'uppercase', /* ⚠ uppercase xs-body, not caption (weight is 400 not 600) */
          fontFamily: 'var(--font-body)',
        }}>
          Patient Details
        </span>
        <CollapseToggle collapsed={collapsed} onClick={onToggle} />
      </div>
      {!collapsed && (
        <div style={{
          fontSize: 'var(--text-xs-size)', fontWeight: 'var(--text-xs-weight)', lineHeight: 'var(--text-xs-lh)', letterSpacing: '0.01em',
          fontFamily: 'var(--font-body)',
        }}>
          <p style={{ ...valueStyle, margin: 0 }}>{patient.age}, {patient.sex}, {patient.ethnicity}</p>
          <p style={{ margin: 0 }}><span style={labelStyle}>DOB</span> <span style={valueStyle}>{patient.dob}</span></p>
          <p style={{ margin: 0 }}><span style={labelStyle}>Member since</span> <span style={valueStyle}>{patient.memberSince} ({patient.membershipType})</span></p>
          <p style={{ margin: 0 }}><span style={labelStyle}>Healthie ID</span> <span style={valueStyle}>{patient.healthieId}</span></p>
        </div>
      )}
    </div>
  )
}

// ─── Section A — Visit Card (C_Card/Visits) ───────────────────────────────

function VisitCard({ visit }: { visit: SidebarPatient['nextVisit'] }) {
  return (
    <chakra.div
      css={{
        background: '{colors.canvas}',
        border: '1px solid', borderColor: '{colors.cloud}',
        borderRadius: '{radii.card}',
        boxShadow: '{shadows.card}',
        fontFamily: '{fonts.body}',
      }}
      style={{ width: '100%', padding: '16px 16px 4px', display: 'flex', flexDirection: 'column' }}
    >
      {/* Wrapper — tag row + labels + quote (gap 16) */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-s)' }}>
        {/* Tag row */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-xs)' }}>
          <Chip variant="neutral">{visit.date}</Chip>
          <Chip variant="aqua">{visit.status}</Chip>
        </div>
        {/* Labels */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ fontSize: 'var(--text-sm-size)', lineHeight: 'var(--text-sm-lh)', letterSpacing: '0.01em', color: 'var(--blue-grey)' }}>
            {visit.type}
          </span>
          <span style={{
            fontFamily: 'var(--font-heading)', fontWeight: 'var(--text-h3-weight)',
            fontSize: 'var(--text-h3-size)', lineHeight: 'var(--text-h3-lh)', color: 'var(--evergreen)',
          }}>
            {visit.provider}
          </span>
        </div>
        {/* Quote */}
        {visit.quote && (
          <p style={{
            margin: 0,
            fontSize: 'var(--text-sm-size)', lineHeight: 'var(--text-sm-lh)', letterSpacing: '0.01em',
            color: 'var(--blue-grey)',
          }}>
            &ldquo;{visit.quote}&rdquo;
          </p>
        )}
        {/* CTA stack — careOS primary→ui solid, secondary→ui outline (clean maps) */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-xs)' }}>
          <Button variant="solid" style={{ width: '100%' }}>Join Visit</Button>
          <Button variant="outline" style={{ width: '100%', gap: 6 }}><FileIcon /> SOAP Note</Button>
        </div>
      </div>

      {/* Mark Status disclosure — careOS tertiary (one-off) */}
      <TertiaryButton endIcon={<ChevronDown />} style={{ marginTop: 'var(--space-s)' }}>
        Mark Status
      </TertiaryButton>
    </chakra.div>
  )
}

// ─── Section B — Action Tile ──────────────────────────────────────────────

function ActionTile({ icon, label, onClick }: { icon: ReactNode; label: string; onClick?: () => void }) {
  return (
    <IconTileButton startIcon={icon} endIcon={<ChevronRight />} onClick={onClick}>
      {label}
    </IconTileButton>
  )
}

// ─── Section C — Patient Details list (C_List/Details) ───────────────────

function DetailRow({ label, value, moreLabel }: { label: string; value: string; moreLabel?: string }) {
  return (
    <div style={{
      width: '100%',
      borderTop: '1px solid var(--steel)',
      paddingTop: 'var(--space-xs)',
      display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
      gap: 'var(--space-s)',
      fontFamily: 'var(--font-body)',
    }}>
      <span style={{
        width: 84, flexShrink: 0,
        fontSize: 'var(--text-sm-size)', fontWeight: 'var(--text-sm-weight)', lineHeight: 'var(--text-sm-lh)', letterSpacing: '0.01em',
        color: 'var(--blue-grey)',
      }}>
        {label}
      </span>
      <span style={{
        flex: 1, minWidth: 0, textAlign: 'right',
        fontSize: 'var(--text-sm-b-size)', fontWeight: 'var(--text-sm-b-weight)', lineHeight: 'var(--text-sm-b-lh)', letterSpacing: '0.01em',
        color: 'var(--seafoam)',
      }}>
        {value}
        {moreLabel && (
          <> <span style={{ color: 'var(--aqua)' }}>{moreLabel}</span></>
        )}
      </span>
      <Pressable
        type="button"
        aria-label="Row actions"
        flexShrink={0}
        w="16px"
        h="16px"
        p={0}
        color="steel"
        display="inline-flex"
        alignItems="center"
        justifyContent="center"
      >
        <MoreVerticalIcon />
      </Pressable>
    </div>
  )
}

// ─── Section D — Care Team row ────────────────────────────────────────────

function CareTeamRow({ member }: { member: { initials: string; name: string; role: string; bg: string } }) {
  return (
    <Pressable type="button" className="hover-bg-feather" w="100%" p={0} bg="transparent" display="flex" flexDirection="row" alignItems="center" justifyContent="space-between" gap="var(--space-s)" textAlign="left">
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-s)' }}>
        <span style={{
          width: 48, height: 48, borderRadius: '50%',
          background: member.bg, color: '#fff',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          fontFamily: 'var(--font-heading)', fontSize: 18, fontWeight: 400, /* lg — on-grid since h4 moved 19px → 20px */
          flexShrink: 0,
        }}>
          {member.initials}
        </span>
        <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <span className="md-body" style={{ color: 'var(--evergreen)' }}>{member.name}</span>
          <span style={{ fontSize: 'var(--text-sm-size)', lineHeight: 'var(--text-sm-lh)', letterSpacing: '0.01em', color: 'var(--blue-grey)' }}>
            {member.role}
          </span>
        </span>
      </span>
      <span style={{ display: 'inline-flex', color: 'var(--evergreen)' }}>
        <ChevronRight />
      </span>
    </Pressable>
  )
}

// ─── Section heading (shared) ────────────────────────────────────────────

function SectionHeading({ children, withTopBorder = true }: { children: ReactNode; withTopBorder?: boolean }) {
  return (
    <div style={{
      width: '100%',
      borderTop: withTopBorder ? '1px solid var(--steel)' : 'none',
      paddingTop: withTopBorder ? 'var(--space-xs)' : 0,
      fontSize: 'var(--text-sm-size)', fontWeight: 'var(--text-sm-weight)', lineHeight: 'var(--text-sm-lh)', letterSpacing: '0.01em',
      color: 'var(--blue-grey)', fontFamily: 'var(--font-body)',
    }}>
      {children}
    </div>
  )
}

// ─── Section G — Visit-history row (C_List/Visit billing) ────────────────

const STATUS_TO_CHIP: Record<MeetingStatus, { variant: ChipVariant; label: string }> = {
  upcoming: { variant: 'neutral', label: 'Upcoming' },
  billed:   { variant: 'mint',    label: 'Billed' },
  'no-show':{ variant: 'ballet',  label: 'No Show' },
}

function VisitHistoryRow({ row }: { row: SidebarPatient['meetingHistory'][number] }) {
  const tag = STATUS_TO_CHIP[row.status]
  return (
    <Pressable
      type="button"
      className="hover-bg-feather"
      flexDirection="row"
      alignItems="center"
      css={{
        background: '{colors.canvas}',
        border: '1px solid', borderColor: '{colors.cloud}',
        borderRadius: '{radii.card}',
        fontFamily: '{fonts.body}',
        cursor: 'pointer',
      }}
      style={{
        width: '100%',
        padding: '8px 8px 8px 12px',
        display: 'flex', justifyContent: 'space-between',
        textAlign: 'left',
      }}
    >
      <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 'var(--space-xs)' }}>
        <span style={{ fontSize: 'var(--text-sm-b-size)', fontWeight: 'var(--text-sm-b-weight)', lineHeight: 'var(--text-sm-b-lh)', letterSpacing: '0.01em', color: 'var(--seafoam)' }}>
          {row.date}
        </span>
        <span style={{ fontSize: 'var(--text-sm-size)', fontWeight: 'var(--text-sm-weight)', lineHeight: 'var(--text-sm-lh)', letterSpacing: '0.01em', color: 'var(--blue-grey)' }}>
          {row.type}
        </span>
      </span>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        <Chip variant={tag.variant}>{tag.label}</Chip>
        <span style={{ display: 'inline-flex', color: 'var(--evergreen)' }}>
          <ChevronRight />
        </span>
      </span>
    </Pressable>
  )
}

// ─── Top-level component ─────────────────────────────────────────────────

export function PatientSidebar({ patient, collapsed = false, onToggleCollapse }: Props) {
  if (collapsed) {
    // Slim rail: only the reopen toggle, anchored to the right edge.
    return (
      <chakra.aside
        css={{ background: '{colors.canvas}', borderLeft: '1px solid', borderLeftColor: '{colors.cloud}' }}
        style={{ width: 48, flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', padding: 'var(--space-s) 0' }}
      >
        <Pressable
          type="button"
          onClick={onToggleCollapse}
          aria-label="Expand patient rail"
          w="32px"
          h="32px"
          borderRadius="card"
          bg="seafoam"
          color="white"
          display="inline-flex"
          alignItems="center"
          justifyContent="center"
        >
          <PanelToggleIcon collapsed={true} />
        </Pressable>
      </chakra.aside>
    )
  }

  return (
    <chakra.aside
      css={{ borderLeft: '1px solid', borderLeftColor: '{colors.cloud}' }}
      style={{ width: 298, flexShrink: 0, display: 'flex', flexDirection: 'column', overflowY: 'auto' }}
    >
      <PatientDetailsHeader patient={patient} collapsed={false} onToggle={onToggleCollapse} />

      {/* Gradient column with the rest of the rail content */}
      <div style={{
        flex: 1,
        background: 'var(--gradient-rail)',
        padding: 'var(--space-s)',
        display: 'flex', flexDirection: 'column', alignItems: 'stretch',
        gap: 'var(--space-32)',
      }}>
        {/* A — Visit card */}
        <VisitCard visit={patient.nextVisit} />

        {/* B — Action tiles */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-xs)' }}>
          <ActionTile icon={<ClipboardIcon />} label="Charting" />
          <ActionTile icon={<MessageIcon />}   label="Message Patient" />
        </div>

        {/* C — Detail rows */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-s)' }}>
          {patient.detailRows.map(row => (
            <DetailRow key={row.label} {...row} />
          ))}
        </div>

        {/* D — Care Team */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-s)' }}>
          <SectionHeading>Care Team</SectionHeading>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-s)' }}>
            {patient.careTeam.map(member => (
              <CareTeamRow key={member.name} member={member} />
            ))}
          </div>
        </div>

        {/* E — Subscription & Account */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-xs)' }}>
          <SectionHeading>Subscription &amp; Account</SectionHeading>
          <ul style={{
            margin: 0, paddingLeft: 18,
            fontFamily: 'var(--font-body)',
            fontSize: 'var(--text-sm-b-size)', fontWeight: 'var(--text-sm-b-weight)', lineHeight: 'var(--text-sm-b-lh)', letterSpacing: '0.01em',
            color: 'var(--seafoam)',
          }}>
            {patient.subscriptionAccount.map((item, i) => (
              <li key={i} style={{ marginBottom: i === patient.subscriptionAccount.length - 1 ? 0 : 2 }}>
                {item}
              </li>
            ))}
          </ul>
        </div>

        {/* F — Risk Stratification History */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-xs)' }}>
          <SectionHeading>Risk Stratification History</SectionHeading>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {patient.riskHistory.map((row, i) => (
              <div key={i} style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                width: '100%',
              }}>
                <RiskDots level={row.level} />
                <span style={{ fontSize: 'var(--text-sm-size)', fontWeight: 'var(--text-sm-weight)', lineHeight: 'var(--text-sm-lh)', letterSpacing: '0.01em', color: 'var(--seafoam)' }}>
                  {row.date}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* G — Meeting & Billing History */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-s)' }}>
          <SectionHeading>Meeting &amp; Billing History</SectionHeading>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-xs)' }}>
            {patient.meetingHistory.map((row, i) => (
              <VisitHistoryRow key={i} row={row} />
            ))}
          </div>
        </div>

        {/* Bottom spacer (40 px) */}
        <div style={{ height: 40, flexShrink: 0 }} />
      </div>
    </chakra.aside>
  )
}
